import React, { useState, useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachMoney as SalaryIcon,
  Search as SearchIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as PayrollIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { useEmployees, useDeleteEmployee, useToggleEmployeeStatus } from '../hooks/useEmployee';
import { Employee } from '../types';
import EmployeeFormDialog from '../components/EmployeeFormDialog';
import SalaryAdjustmentDialog from '../components/SalaryAdjustmentDialog';
import ConfirmDialog from '../components/ConfirmDialog';

function initials(name: string) {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

const AVATAR_COLORS = [
  '#2196f3',
  '#66bb6a',
  '#ff9800',
  '#ab47bc',
  '#ef5350',
  '#26c6da',
  '#ffa726',
  '#8d6e63',
];
function avatarColor(name: string) {
  let n = 0;
  for (let i = 0; i < name.length; i++) n += name.charCodeAt(i);
  return AVATAR_COLORS[n % AVATAR_COLORS.length];
}

const ACCENT = '#2196f3';

const transformEmployee = (
  emp: any
): Employee & {
  name: string;
  phone: string;
  hired_date: string;
  status: 'active' | 'inactive';
} => ({
  ...emp,
  name: `${emp.first_name} ${emp.last_name}`.trim(),
  phone: emp.phone_number,
  hired_date: emp.hire_date || new Date().toISOString(),
  status: emp.is_active === 1 ? 'active' : 'inactive',
});

const Employees: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [salaryDialogOpen, setSalaryDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, error, isFetching } = useEmployees({
    is_active: statusFilter === 'all' ? undefined : statusFilter === 'active' ? 1 : 0,
    search: debouncedSearch || undefined,
    page,
    per_page: pageSize,
    sort_by: sortModel[0]?.field ?? 'created_at',
    sort_order: sortModel[0]?.sort ?? 'desc',
  });

  const transformedData = data?.data?.map(transformEmployee) || [];

  const payrollStats = useMemo(() => {
    const allEmployees = data?.data || [];
    const activeEmployees = allEmployees.filter((emp: any) => emp.is_active === 1);

    const parseAmount = (amount: string | number): number => {
      if (typeof amount === 'number') return amount;
      return parseFloat(amount.replace(/[^0-9.-]+/g, '')) || 0;
    };

    const totalActivePayroll = activeEmployees.reduce((sum: number, emp: any) => {
      return sum + parseAmount(emp.salary);
    }, 0);

    const avgActiveSalary =
      activeEmployees.length > 0 ? totalActivePayroll / activeEmployees.length : 0;

    return {
      totalActivePayroll,
      avgActiveSalary,
      activeCount: activeEmployees.length,
      inactiveCount: allEmployees.length - activeEmployees.length,
    };
  }, [data?.data]);

  const deleteEmployee = useDeleteEmployee();
  const toggleStatus = useToggleEmployeeStatus();

  const handleAddEmployee = () => {
    setSelectedEmployee(null);
    setFormDialogOpen(true);
  };

  const handleEditEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
    setFormDialogOpen(true);
  };

  const handleAdjustSalary = (employee: Employee) => {
    setSelectedEmployee(employee);
    setSalaryDialogOpen(true);
  };

  const handleToggleStatus = async (employee: Employee) => {
    try {
      await toggleStatus.mutateAsync(employee.id);
    } catch (err) {
      console.error('Failed to toggle status:', err);
    }
  };

  const handleDeleteClick = (employee: Employee) => {
    setSelectedEmployee(employee);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedEmployee) {
      try {
        await deleteEmployee.mutateAsync(selectedEmployee.id);
        setDeleteDialogOpen(false);
        setSelectedEmployee(null);
      } catch (err) {
        console.error('Failed to delete employee:', err);
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Employee',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => {
        const color = avatarColor(params.value);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.72rem',
                fontWeight: 700,
                bgcolor: `${color}22`,
                color,
                border: `1px solid ${color}44`,
                flexShrink: 0,
              }}
            >
              {initials(params.value)}
            </Avatar>
            <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }}>
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 160,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            {params.value}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>
            —
          </Typography>
        ),
    },
    {
      field: 'salary',
      headerName: 'Salary',
      width: 140,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: '#ff9800' }}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'hired_date',
      headerName: 'Hire Date',
      width: 110,
      renderCell: (params: GridRenderCellParams<Employee>) => {
        const date = params.row.hired_date || params.row.hire_date;
        return (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
            {date
              ? new Date(date).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })
              : '—'}
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams<Employee>) => {
        const isActive = params.row.status === 'active';
        return (
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.68rem',
              fontWeight: 700,
              bgcolor: isActive ? 'rgba(102,187,106,0.15)' : 'rgba(255,255,255,0.08)',
              color: isActive ? '#66bb6a' : 'rgba(255,255,255,0.4)',
              border: `1px solid ${isActive ? 'rgba(102,187,106,0.3)' : 'rgba(255,255,255,0.1)'}`,
            }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 130,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Employee>) => (
        <Stack direction="row" spacing={0.25}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditEmployee(params.row)}
              sx={{
                color: 'rgba(255,152,0,0.7)',
                '&:hover': { color: '#ff9800', bgcolor: 'rgba(255,152,0,0.1)' },
              }}
            >
              <EditIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Adjust Salary">
            <IconButton
              size="small"
              onClick={() => handleAdjustSalary(params.row)}
              sx={{
                color: 'rgba(33,150,243,0.7)',
                '&:hover': { color: ACCENT, bgcolor: 'rgba(33,150,243,0.1)' },
              }}
            >
              <SalaryIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.status === 'active' ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => handleToggleStatus(params.row)}
              sx={
                params.row.status === 'active'
                  ? {
                      color: 'rgba(239,83,80,0.7)',
                      '&:hover': { color: '#ef5350', bgcolor: 'rgba(239,83,80,0.1)' },
                    }
                  : {
                      color: 'rgba(102,187,106,0.7)',
                      '&:hover': { color: '#66bb6a', bgcolor: 'rgba(102,187,106,0.1)' },
                    }
              }
            >
              {params.row.status === 'active' ? (
                <InactiveIcon sx={{ fontSize: 15 }} />
              ) : (
                <ActiveIcon sx={{ fontSize: 15 }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row)}
              sx={{
                color: 'rgba(244,67,54,0.6)',
                '&:hover': { color: '#f44336', bgcolor: 'rgba(244,67,54,0.1)' },
              }}
            >
              <DeleteIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', p: 2.5 }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 2,
            '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg,${ACCENT},#42a5f5)` },
          }}
        />
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 2 }}>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
            Employees
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            Manage staff, payroll and employment status
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddEmployee}
          sx={{
            background: `linear-gradient(135deg,${ACCENT},#42a5f5)`,
            color: '#fff',
            px: 2,
            '&:hover': { background: 'linear-gradient(135deg,#1976d2,#2196f3)' },
          }}
        >
          Add Employee
        </Button>
      </Box>

      {/* Stats strip */}
      <Paper
        sx={{
          mb: 2,
          px: 0,
          py: 0,
          background: 'linear-gradient(135deg,#161616,#1e1e1e)',
          border: `1px solid rgba(33,150,243,0.12)`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
          {[
            {
              label: 'Active Payroll',
              value: `GH₵ ${payrollStats.totalActivePayroll.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
              sub: `${payrollStats.activeCount} staff`,
              icon: <PayrollIcon sx={{ fontSize: 15 }} />,
              color: '#ff9800',
            },
            {
              label: 'Active',
              value: payrollStats.activeCount,
              sub: `${payrollStats.inactiveCount} inactive`,
              icon: <PeopleIcon sx={{ fontSize: 15 }} />,
              color: '#66bb6a',
            },
            {
              label: 'Avg Salary',
              value: `GH₵ ${payrollStats.avgActiveSalary.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
              sub: 'per active staff',
              icon: <TrendingUpIcon sx={{ fontSize: 15 }} />,
              color: ACCENT,
            },
            {
              label: 'Annual Payroll',
              value: `GH₵ ${(payrollStats.totalActivePayroll * 26).toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
              sub: 'projected',
              icon: <SalaryIcon sx={{ fontSize: 15 }} />,
              color: '#ab47bc',
            },
          ].map((s, i, arr) => (
            <React.Fragment key={s.label}>
              <Box
                sx={{
                  flex: 1,
                  py: 1.5,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '8px',
                    bgcolor: `${s.color}18`,
                    color: s.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {s.icon}
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', display: 'block' }}
                  >
                    {s.label.toUpperCase()}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: s.color, lineHeight: 1.1, fontSize: '1rem' }}
                    >
                      {s.value}
                    </Typography>
                    {s.sub && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}
                      >
                        {s.sub}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
              {i < arr.length - 1 && (
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: 'rgba(33,150,243,0.08)' }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Paper>

      {/* Table */}
      <Paper
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          border: `1px solid rgba(33,150,243,0.15)`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: 'linear-gradient(135deg,#161616,#1e1e1e)',
            borderBottom: `1px solid rgba(33,150,243,0.1)`,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ color: 'rgba(255,255,255,0.7)', flexShrink: 0 }}
          >
            Records
          </Typography>
          <Chip
            label={data?.meta?.total ?? transformedData.length}
            size="small"
            sx={{
              bgcolor: 'rgba(33,150,243,0.1)',
              color: ACCENT,
              fontWeight: 700,
              height: 18,
              fontSize: '0.7rem',
            }}
          />
          <Box flex={1} />
          <Select
            size="small"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
              setPage(1);
            }}
            sx={{
              height: 32,
              fontSize: '0.8rem',
              color: 'rgba(255,255,255,0.7)',
              bgcolor: 'rgba(255,255,255,0.04)',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(33,150,243,0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(33,150,243,0.4)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
              '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.4)' },
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
          <TextField
            size="small"
            placeholder="Search employees…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.3)' }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              width: 220,
              '& .MuiOutlinedInput-root': {
                height: 32,
                fontSize: '0.8rem',
                bgcolor: 'rgba(255,255,255,0.04)',
                '& fieldset': { borderColor: 'rgba(33,150,243,0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(33,150,243,0.4)' },
                '&.Mui-focused fieldset': { borderColor: ACCENT },
              },
              '& input': { color: '#fff', py: 0.5 },
              '& input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
            }}
          />
        </Box>

        <DataGrid
          rows={transformedData}
          columns={columns}
          loading={isLoading}
          pagination
          paginationMode="server"
          rowCount={data?.meta?.total || 0}
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page + 1);
            setPageSize(model.pageSize);
          }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            setPage(1);
          }}
          pageSizeOptions={[10, 15, 25, 50]}
          disableRowSelectionOnClick
          rowHeight={44}
          sx={{
            flex: 1,
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(255,255,255,0.04)',
              color: '#fff',
              fontSize: '0.82rem',
            },
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'rgba(33,150,243,0.07)',
              borderBottom: '1px solid rgba(33,150,243,0.15)',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
              fontSize: '0.68rem',
              letterSpacing: '0.7px',
              color: 'rgba(33,150,243,0.7)',
              textTransform: 'uppercase',
            },
            '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(33,150,243,0.05)' },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(33,150,243,0.12)',
              bgcolor: 'rgba(33,150,243,0.03)',
              minHeight: 40,
            },
            '& .MuiTablePagination-root': { color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' },
            '& .MuiTablePagination-selectIcon': { color: 'rgba(255,255,255,0.4)' },
          }}
        />
      </Paper>

      {error && (
        <Typography sx={{ color: '#f44336', mt: 1, fontSize: '0.8rem' }}>
          Error loading employees: {error.message}
        </Typography>
      )}

      <EmployeeFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
      />

      <SalaryAdjustmentDialog
        open={salaryDialogOpen}
        onClose={() => {
          setSalaryDialogOpen(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedEmployee(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Employee"
        message={`Are you sure you want to delete ${selectedEmployee?.name}? This action cannot be undone.`}
        loading={deleteEmployee.isPending}
      />
    </Box>
  );
};

export default Employees;
